import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Label,
  Modal,
  Select,
  Spinner,
  TextInput,
  ToggleSwitch,
} from "flowbite-react";
import {
  HiArrowRight,
  HiChevronDown,
  HiChevronUp,
  HiOutlineTrash,
  HiPlus,
  HiSparkles,
} from "react-icons/hi";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";
import SimpleToggle from "../components/common/SimpleToggle";
import axios from "../config/axiosInstance";
import { CRM, USERS, TAGS } from "../config/apiConfig";
import { useLocation } from "react-router-dom";
import { FiEye, FiTrash } from "react-icons/fi";

const TRIGGER_OPTIONS = [
  { value: "stage_enter", label: "Ao entrar na etapa" },
  { value: "stage_exit", label: "Ao sair da etapa" },
  { value: "card_created", label: "Ao criar um card" },
  { value: "card_stale", label: "Card parado" },
  { value: "card_won", label: "Card marcado como ganho" },
  { value: "card_lost", label: "Card marcado como perdido" },
];

const ACTION_TYPES = [
  { value: "move_card", label: "Mover card (outro funil)" },
  { value: "set_lead_status", label: "Atualizar status do cliente" },
  { value: "send_email", label: "Enviar e-mail" },
  { value: "assign_responsible", label: "Atribuir responsável" },
  { value: "create_task", label: "Criar tarefa" },
  { value: "apply_tag", label: "Aplicar etiqueta (tag)" },
  { value: "log_history", label: "Registrar Anotação" },
];

const STATUS_OPTIONS = [
  { value: "working", label: "Em andamento" },
  { value: "qualified", label: "Qualificado" },
  { value: "converted", label: "Convertido" },
  { value: "unqualified", label: "Não qualificado" },
  { value: "lost", label: "Perdido" },
];

const EMAIL_RECIPIENTS = [
  { value: "responsible", label: "Responsável" },
  { value: "custom", label: "E-mail personalizado" },
];

const TASK_ACTION_PRESETS = [
  { key: "ligacao", label: "Ligação" },
  { key: "reuniao", label: "Reunião" },
  { key: "email", label: "E-mail" },
  { key: "visita", label: "Visita" },
  { key: "followup", label: "Follow-up" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "proposta", label: "Proposta" },
];

const defaultTaskNoteFor = (type) => {
  switch (type) {
    case "ligacao":
      return "Ligar para o cliente";
    case "reuniao":
      return "Agendar reunião com o cliente";
    case "email":
      return "Enviar e-mail para o cliente";
    case "visita":
      return "Agendar visita com o cliente";
    case "followup":
      return "Fazer follow-up";
    case "whatsapp":
      return "Enviar mensagem no WhatsApp";
    case "proposta":
      return "Enviar proposta para o cliente";
    default:
      return "Definir próxima ação";
  }
};

const calculateDueDateFromOffset = (days = 0, hours = 0) => {
  const millis =
    (Number(days) || 0) * 86400000 + (Number(hours) || 0) * 3600000;
  if (!millis) return "";
  const now = new Date();
  const dueDate = new Date(now.getTime() + millis);
  dueDate.setMinutes(0, 0, 0);
  return dueDate.toISOString();
};

const getTomorrowDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

const extractList = (payload, key) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (key && Array.isArray(payload[key])) return payload[key];
  return [];
};

const AutomationPage = () => {
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const [automations, setAutomations] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyTags, setCompanyTags] = useState([]);
  const [tagsByFunnel, setTagsByFunnel] = useState({});
  const [newActionType, setNewActionType] = useState(ACTION_TYPES[0].value);
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [showDestinationSection, setShowDestinationSection] = useState(false);
  const [logs, setLogs] = useState([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [loadingFunnels, setLoadingFunnels] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState("");
  const [logError, setLogError] = useState("");
  const [form, setForm] = useState({
    name: "",
    triggers: [],
    targetFunnelId: "",
    targetStageId: "",
    isActive: true,
    settings: {},
  });
  const [activeTriggerIndex, setActiveTriggerIndex] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [deletionRequest, setDeletionRequest] = useState(null);
  const location = useLocation();
  const funnelParam = useMemo(
    () => new URLSearchParams(location.search).get("f"),
    [location.search]
  );

  const stageOptions = useMemo(() => {
    if (!selectedFunnelId) return [];
    const funnel = funnels.find((item) => item.id === selectedFunnelId);
    return Array.isArray(funnel?.stages) ? funnel.stages : [];
  }, [funnels, selectedFunnelId]);

  const targetFunnelStages = useMemo(() => {
    if (!form.targetFunnelId) return [];
    const funnel = funnels.find((item) => item.id === form.targetFunnelId);
    return Array.isArray(funnel?.stages) ? funnel.stages : [];
  }, [funnels, form.targetFunnelId]);

  const getTagsForFunnelId = (funnelId) => {
    if (!funnelId) return [];
    return (
      tagsByFunnel[funnelId] ||
      (funnelId === selectedFunnelId ? companyTags : [])
    );
  };
  const getActionTags = (action) => {
    const funnelId =
      action.payload?.targetFunnelId || form.targetFunnelId || selectedFunnelId;
    return getTagsForFunnelId(funnelId);
  };

  const effectiveTargetFunnelId = showDestinationSection
    ? form.targetFunnelId
    : selectedFunnelId;
  const destinationStageOptions = showDestinationSection
    ? targetFunnelStages
    : stageOptions;

  const activeTrigger =
    form.triggers[activeTriggerIndex] || form.triggers[0] || null;
  const activeTriggerActions = activeTrigger?.actions || [];
  const selectedTriggerStage = useMemo(
    () =>
      stageOptions.find((stage) => stage.id === activeTrigger?.stageId) || null,
    [stageOptions, activeTrigger?.stageId]
  );

  const selectedDestinationStage = useMemo(
    () =>
      destinationStageOptions.find((stage) => stage.id === form.targetStageId),
    [destinationStageOptions, form.targetStageId]
  );

  const triggerEventLabel = useMemo(
    () =>
      TRIGGER_OPTIONS.find((opt) => opt.value === activeTrigger?.event)
        ?.label || "Evento",
    [activeTrigger?.event]
  );

  const openTriggerEditor = (index) => {
    setActiveTriggerIndex(index);
    setViewMode("builder");
  };

  const handleCancelDeletion = () => {
    setDeletionRequest(null);
  };

  const handleConfirmDeletion = () => {
    if (!deletionRequest) return;
    if (deletionRequest.type === "trigger") {
      performTriggerRemoval(deletionRequest.triggerIndex);
      return;
    }
    performActionRemoval(
      deletionRequest.triggerIndex,
      deletionRequest.actionIndex
    );
  };

  const destinationFunnelName = useMemo(() => {
    if (showDestinationSection && form.targetFunnelId) {
      return (
        funnels.find((funnel) => funnel.id === form.targetFunnelId)?.name || ""
      );
    }
    if (!showDestinationSection && selectedFunnelId) {
      return (
        funnels.find((funnel) => funnel.id === selectedFunnelId)?.name || ""
      );
    }
    return "";
  }, [showDestinationSection, form.targetFunnelId, selectedFunnelId, funnels]);

  const monitoredFunnelName = useMemo(
    () =>
      selectedFunnelId
        ? funnels.find((funnel) => funnel.id === selectedFunnelId)?.name || ""
        : "",
    [funnels, selectedFunnelId]
  );

  const formatActionSummary = (action, trigger) => {
    if (!action) return "—";
    if (action.type === "stage_event") {
      const stageId =
        trigger?.stageId ||
        action.payload?.stageId ||
        action.payload?.targetStageId;
      const stage = funnels
        .flatMap((f) => f.stages ?? [])
        .find((s) => s.id === stageId);
      const eventValue =
        trigger?.event ||
        action.payload?.event ||
        (action.payload?.event === "stage_exit" ? "stage_exit" : "stage_enter");
      const eventLabel =
        eventValue === "stage_exit" ? "Ao sair da etapa" : "Ao entrar na etapa";
      return `${eventLabel} ${stage?.name || "—"}`;
    }
    if (action.type === "move_card") {
      const stageId = action.payload?.targetStageId;
      const stage = funnels
        .flatMap((f) => f.stages ?? [])
        .find((s) => s.id === stageId);
      return `Mover para ${stage?.name || "—"}`;
    }
    if (action.type === "set_lead_status") {
      return `Define status: ${action.payload?.status || "—"}`;
    }
    if (action.type === "send_email") {
      return `Enviar e-mail para ${action.payload?.recipient || "lead"}`;
    }
    if (action.type === "assign_responsible") {
      return `Atribuir ${
        users.find(
          (u) => String(u.id) === String(action.payload?.responsibleId)
        )?.name || "responsável"
      }`;
    }
    if (action.type === "create_task") {
      const days = action.payload?.dueInDays
        ? `${action.payload.dueInDays}d`
        : "";
      const hours = action.payload?.dueInHours
        ? `${action.payload.dueInHours}h`
        : "";
      const offsetLabel =
        days || hours
          ? ` — prazo ${[days, hours].filter(Boolean).join(" ")}`
          : "";
      return `Criar tarefa "${
        action.payload?.taskType || "automação"
      }"${offsetLabel}`;
    }
    if (action.type === "apply_tag") {
      const tagName = getActionTags(action).find(
        (tag) => String(tag.id) === String(action.payload?.tagId)
      )?.name;
      return `Aplicar etiqueta ${tagName || ""}`.trim();
    }
    if (action.type === "log_history") {
      return `"${(action.payload?.text || "").slice(0, 40)}"`;
    }
    return action.type;
  };

  const getTriggerStageName = (trigger) =>
    stageOptions.find((stage) => stage.id === trigger?.stageId)?.name || "—";

  const getTriggerEventLabel = (event) =>
    TRIGGER_OPTIONS.find((opt) => opt.value === event)?.label || "Evento";

  const breadcrumbItems = useMemo(() => {
    const currentFunnel = funnels.find((item) => item.id === selectedFunnelId);
    const currentAutomation =
      editingAutomation ||
      automations.find((item) => item.id === selectedAutomationId);
    const funnelLabel = currentFunnel?.name || "Pipeline central";
    const automationLabel = "Automações";
    return [
      {
        label: funnelLabel,
        href: selectedFunnelId ? `/crm?f=${selectedFunnelId}` : undefined,
      },
      { label: automationLabel, current: true },
    ];
  }, [
    funnels,
    selectedFunnelId,
    editingAutomation,
    automations,
    selectedAutomationId,
  ]);

  useEffect(() => {
    loadFunnels();
  }, []);

  useEffect(() => {
    if (!selectedFunnelId) return;
    fetchAutomations(selectedFunnelId);
  }, [selectedFunnelId]);

  useEffect(() => {
    if (!stageOptions.length) return;
    setForm((prev) => {
      const defaultStageId = stageOptions[0]?.id || "";
      const normalized =
        prev.triggers.length > 0
          ? prev.triggers.map((trigger) => ({
              stageId: stageOptions.some(
                (stage) => stage.id === trigger.stageId
              )
                ? trigger.stageId
                : defaultStageId,
              event: trigger.event || "stage_enter",
            }))
          : [{ stageId: defaultStageId, event: "stage_enter" }];
      const hasChanged =
        normalized.length !== prev.triggers.length ||
        normalized.some(
          (trigger, idx) =>
            trigger.stageId !== prev.triggers[idx]?.stageId ||
            trigger.event !== prev.triggers[idx]?.event
        );
      if (!hasChanged) return prev;
      return { ...prev, triggers: normalized };
    });
  }, [stageOptions]);

  useEffect(() => {
    if (!showDestinationSection) return;
    if (!form.targetFunnelId || !targetFunnelStages.length) return;
    setForm((prev) => ({
      ...prev,
      targetStageId: prev.targetStageId || targetFunnelStages[0]?.id || "",
    }));
  }, [form.targetFunnelId, targetFunnelStages, showDestinationSection]);

  useEffect(() => {
    if (showDestinationSection) return;
    if (!selectedFunnelId || !stageOptions.length) return;
    setForm((prev) => ({
      ...prev,
      targetFunnelId: selectedFunnelId,
      targetStageId: prev.targetStageId || stageOptions[0]?.id || "",
    }));
  }, [selectedFunnelId, stageOptions, showDestinationSection]);

  useEffect(() => {
    if (!selectedAutomationId) return;
    fetchLogs(selectedAutomationId);
  }, [selectedAutomationId]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (!selectedFunnelId) {
      setCompanyTags([]);
      return;
    }
    loadTagsForFunnel(selectedFunnelId);
  }, [selectedFunnelId, funnels]);

  const loadFunnels = async () => {
    setLoadingFunnels(true);
    try {
      const { data } = await axios.get(CRM.FUNNELS);
      const normalized = Array.isArray(data.data) ? data.data : [];
      setFunnels(normalized);
      if (normalized.length) {
        const preferredFunnel =
          funnelParam &&
          normalized.find((item) => String(item.id) === funnelParam);
        const targetFunnelId = preferredFunnel?.id || normalized[0]?.id;
        if (targetFunnelId) {
          setSelectedFunnelId((prev) => prev || targetFunnelId);
        }
      }
    } catch (err) {
      console.error(err);
      setError("Não foi possível carregar os funis.");
    } finally {
      setLoadingFunnels(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
      const normalized = extractList(res?.data?.data, "users");
      setUsers(normalized);
    } catch (err) {
      console.error("Erro ao carregar usuários:", err);
    }
  };

  const loadTagsForFunnel = async (funnelId) => {
    if (!funnelId) {
      return [];
    }
    const existing = tagsByFunnel[funnelId];
    if (existing) {
      setCompanyTags((prev) =>
        funnelId === selectedFunnelId ? existing : prev
      );
      return existing;
    }
    const funnel = funnels.find((item) => item.id === funnelId);
    if (!funnel?.companyId) {
      if (funnelId === selectedFunnelId) {
        setCompanyTags([]);
      }
      return [];
    }
    try {
      const res = await axios.get(
        `${TAGS.LIST(funnel.companyId)}?itemsPerPage=-1`
      );

      const normalized = extractList(res?.data?.data?.items, "tags");
      setTagsByFunnel((prev) => ({ ...prev, [funnelId]: normalized }));
      if (funnelId === selectedFunnelId) {
        setCompanyTags(normalized);
      }
      return normalized;
    } catch (err) {
      console.error("Erro ao carregar tags:", err);
      if (funnelId === selectedFunnelId) {
        setCompanyTags([]);
      }
      return [];
    }
  };

  const loadAutomationIntoForm = (automation) => {
    if (!automation) {
      setEditingAutomation(null);
      setShowDestinationSection(false);
      setForm((prev) => ({
        ...prev,
        name: "",
        triggers: [],
        targetStageId: "",
        targetFunnelId: "",
        settings: {},
      }));
      setActiveTriggerIndex(0);
      return;
    }
    setEditingAutomation(automation);
    const rawTriggers = Array.isArray(automation.triggers)
      ? automation.triggers
      : [];
    const normalizedActions = (
      Array.isArray(automation.actions) ? automation.actions : []
    ).filter((action) => action?.type !== "stage_event");
    const automationTriggers = rawTriggers
      .filter((trigger) => trigger?.stageId)
      .map((trigger) => ({
        stageId: trigger.stageId,
        event: trigger.event || "stage_enter",
        name: trigger.name || "",
        actions: (Array.isArray(trigger.actions) ? trigger.actions : []).map(
          (action) => ({
            ...action,
            isActive: action.isActive !== false,
            isEditing: false,
          })
        ),
      }));
    if (!automationTriggers.length && automation.triggerStageId) {
      automationTriggers.push({
        stageId: automation.triggerStageId,
        event: automation.triggerEvent || "stage_enter",
        name: automation.name || "",
        actions: normalizedActions.map((action) => ({
          ...action,
          isActive: action.isActive !== false,
        })),
      });
    }
    if (
      automationTriggers.length &&
      !automationTriggers.some(
        (trigger) => trigger.actions && trigger.actions.length
      ) &&
      normalizedActions.length
    ) {
      automationTriggers[0].actions = normalizedActions.map((action) => ({
        ...action,
        isActive: action.isActive !== false,
      }));
    }
    setForm({
      name: automation.name || "",
      triggers: automationTriggers,
      targetFunnelId:
        automation.targetFunnelId || automation.targetStage?.funnel?.id || "",
      targetStageId:
        automation.targetStageId || automation.targetStage?.id || "",
      isActive: automation.isActive,
      settings: automation.settings || {},
    });
    setActiveTriggerIndex(0);
    const sourceFunnelId =
      automation.triggerStage?.funnel?.id || selectedFunnelId || "";
    const destinationFunnelId =
      automation.targetFunnelId || automation.targetStage?.funnel?.id || "";
    const hasExternalDestination =
      Boolean(sourceFunnelId && destinationFunnelId) &&
      String(sourceFunnelId) !== String(destinationFunnelId);
    setShowDestinationSection(hasExternalDestination);
  };

  const fetchAutomations = async (funnelId) => {
    setLoadingRules(true);
    try {
      const { data } = await axios.get(CRM.AUTOMATIONS_LIST(funnelId));
      const normalized = Array.isArray(data.data) ? data.data : [];
      setAutomations(normalized);
      if (!normalized.length) {
        setSelectedAutomationId(null);
        setLogs([]);
        loadAutomationIntoForm(null);
        return;
      }
      const nextSelected = normalized.some(
        (item) => item.id === selectedAutomationId
      )
        ? selectedAutomationId
        : normalized[0].id;
      setSelectedAutomationId(nextSelected);
      const automationToLoad = normalized.find(
        (item) => item.id === nextSelected
      );
      loadAutomationIntoForm(automationToLoad);
    } catch (err) {
      console.error(err);
      setError("Não foi possível listar as automações.");
    } finally {
      setLoadingRules(false);
    }
  };

  const fetchLogs = async (automationId) => {
    if (!automationId) return;
    setLoadingLogs(true);
    setLogError("");
    try {
      const { data } = await axios.get(CRM.AUTOMATIONS_LOGS(automationId));
      const normalizedLogs = Array.isArray(data?.data) ? data.data : [];
      setLogs(normalizedLogs);
    } catch (err) {
      console.error(err);
      setLogError("Não foi possível carregar o histórico.");
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedFunnelId) return;
    if (showDestinationSection && !form.targetFunnelId) {
      setError("Selecione o funil de destino.");
      return;
    }

    setSaving(true);
    setError("");
    const triggerPayload = buildTriggersPayload();
    if (!triggerPayload.length) {
      setError("Configure ao menos um gatilho com ações.");
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: form.name,
        isActive: form.isActive,
        settings: form.settings || {},
        triggers: triggerPayload,
        ...getDestinationPayload(),
      };
      await axios.post(CRM.AUTOMATIONS_CREATE(selectedFunnelId), payload);
      resetForm();
      setViewMode("list");
      fetchAutomations(selectedFunnelId);
    } catch (submitError) {
      console.error(submitError);
      setError(
        submitError?.response?.data?.message ||
          "Não foi possível criar a automação."
      );
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      triggers: [],
      targetFunnelId: "",
      targetStageId: "",
      isActive: true,
      settings: {},
    });
    setActiveTriggerIndex(0);
    setEditingAutomation(null);
    setShowDestinationSection(false);
  };

  const handleSubmit = async () => {
    if (editingAutomation?.id) {
      return handleUpdate();
    }
    return handleCreate();
  };

  const handleSelectAutomation = (automation) => {
    setSelectedAutomationId(automation.id);
    loadAutomationIntoForm(automation);
  };

  const handleUpdate = async () => {
    if (!editingAutomation?.id) {
      return;
    }
    if (showDestinationSection && !form.targetFunnelId) {
      setError("Selecione o funil de destino.");
      return;
    }
    setSaving(true);
    setError("");
    const triggerPayload = buildTriggersPayload();
    if (!triggerPayload.length) {
      setError("Configure ao menos um gatilho com ações.");
      setSaving(false);
      return;
    }
    try {
      await axios.put(CRM.UPDATE_AUTOMATION(editingAutomation.id), {
        name: form.name,
        isActive: form.isActive,
        settings: form.settings || {},
        triggers: triggerPayload,
        ...getDestinationPayload(),
      });
      setViewMode("list");
      fetchAutomations(selectedFunnelId);
    } catch (submitError) {
      console.error(submitError);
      setError(
        submitError?.response?.data?.message ||
          "Não foi possível atualizar a automação."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (automation) => {
    setActionLoading((prev) => ({ ...prev, [automation.id]: true }));
    try {
      await axios.put(CRM.UPDATE_AUTOMATION(automation.id), {
        isActive: !automation.isActive,
      });
      fetchAutomations(selectedFunnelId);
    } catch (toggleError) {
      console.error(toggleError);
      setError("Não foi possível atualizar a automação.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [automation.id]: false }));
    }
  };

  const handleDelete = async (automationId) => {
    setActionLoading((prev) => ({ ...prev, [automationId]: true }));
    try {
      await axios.delete(CRM.DELETE_AUTOMATION(automationId));
      if (selectedAutomationId === automationId) {
        setSelectedAutomationId(null);
        loadAutomationIntoForm(null);
        setLogs([]);
      }
      fetchAutomations(selectedFunnelId);
    } catch (deleteError) {
      console.error(deleteError);
      setError("Não foi possível remover a automação.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [automationId]: false }));
    }
  };

  const handleFunnelChange = (value) => {
    setSelectedFunnelId(value);
  };

  const getDestinationPayload = () => {
    if (!form.targetStageId) {
      return {};
    }
    return {
      targetFunnelId: showDestinationSection
        ? form.targetFunnelId
        : selectedFunnelId,
      targetStageId: form.targetStageId,
    };
  };

  const addTrigger = () => {
    const defaultStageId = stageOptions[0]?.id || "";
    setForm((prev) => {
      const nextTriggers = [
        ...prev.triggers,
        {
          stageId: defaultStageId,
          event: "stage_enter",
          actions: [],
          name: `Gatilho ${prev.triggers.length + 1}`,
        },
      ];
      setActiveTriggerIndex(nextTriggers.length - 1);
      return { ...prev, triggers: nextTriggers };
    });
    setViewMode("builder");
  };

  const updateTrigger = (index, changes) => {
    setForm((prev) => {
      const next = [...prev.triggers];
      next[index] = { ...next[index], ...changes };
      return { ...prev, triggers: next };
    });
  };

  const performTriggerRemoval = (index) => {
    setForm((prev) => {
      if (prev.triggers.length <= 1) return prev;
      const nextTriggers = prev.triggers.filter((_, idx) => idx !== index);
      setActiveTriggerIndex((prevIndex) => {
        if (!nextTriggers.length) return 0;
        if (prevIndex >= nextTriggers.length) {
          return nextTriggers.length - 1;
        }
        if (index < prevIndex) {
          return Math.max(0, prevIndex - 1);
        }
        return prevIndex;
      });
      return { ...prev, triggers: nextTriggers };
    });
    setDeletionRequest(null);
  };

  const promptTriggerRemoval = (index) => {
    setDeletionRequest({ type: "trigger", triggerIndex: index });
  };

  const updateTriggerActions = (triggerIndex, updater) => {
    setForm((prev) => {
      const nextTriggers = [...prev.triggers];
      const trigger = nextTriggers[triggerIndex];
      if (!trigger) return prev;
      const nextActions =
        typeof updater === "function"
          ? updater(trigger.actions || [])
          : updater;
      nextTriggers[triggerIndex] = { ...trigger, actions: nextActions };
      return { ...prev, triggers: nextTriggers };
    });
  };

  const addActionToTrigger = (triggerIndex) => {
    const defaultFunnelId =
      form.targetFunnelId || selectedFunnelId || funnels[0]?.id;
    const defaultStages =
      funnels.find((item) => item.id === defaultFunnelId)?.stages ?? [];
    const fallbackPayload = {
      targetFunnelId: defaultFunnelId,
      targetStageId: defaultStages[0]?.id || "",
      sourceStageId: stageOptions[0]?.id || "",
    };
    const actionPayload = {
      ...fallbackPayload,
      ...buildDefaultActionPayload(newActionType),
    };
    const defaultStageId = stageOptions[0]?.id || "";
    actionPayload.stageId = actionPayload.stageId || defaultStageId;
    actionPayload.event = actionPayload.event || "stage_enter";
    const newAction = {
      id: `${Date.now()}-${newActionType}-${triggerIndex}`,
      type: newActionType,
      payload: actionPayload,
      isActive: true,
      isEditing: true,
    };
    updateTriggerActions(triggerIndex, (prev = []) => {
      const next = [...prev, newAction];
      return next.map((action, idx) => ({ ...action, order: idx }));
    });
  };

  const updateActionInTrigger = (triggerIndex, actionIndex, changes) => {
    setForm((prev) => {
      const nextTriggers = [...prev.triggers];
      const trigger = nextTriggers[triggerIndex];
      if (!trigger) return prev;
      const nextActions = [...(trigger.actions || [])];
      if (!nextActions[actionIndex]) return prev;
      nextActions[actionIndex] = {
        ...nextActions[actionIndex],
        ...changes,
      };
      nextTriggers[triggerIndex] = { ...trigger, actions: nextActions };
      return { ...prev, triggers: nextTriggers };
    });
  };

  const setActionEditing = (triggerIndex, actionIndex, isEditing) => {
    updateActionInTrigger(triggerIndex, actionIndex, { isEditing });
  };

  const updateActionPayload = (triggerIndex, actionIndex, changes) => {
    setForm((prev) => {
      const nextTriggers = [...prev.triggers];
      const trigger = nextTriggers[triggerIndex];
      if (!trigger) return prev;
      const nextActions = [...(trigger.actions || [])];
      if (!nextActions[actionIndex]) return prev;
      nextActions[actionIndex] = {
        ...nextActions[actionIndex],
        payload: { ...nextActions[actionIndex].payload, ...changes },
      };
      nextTriggers[triggerIndex] = { ...trigger, actions: nextActions };
      return { ...prev, triggers: nextTriggers };
    });
  };

  const performActionRemoval = (triggerIndex, actionIndex) => {
    setForm((prev) => {
      const nextTriggers = [...prev.triggers];
      const trigger = nextTriggers[triggerIndex];
      if (!trigger) return prev;
      const nextActions = (trigger.actions || []).filter(
        (_, idx) => idx !== actionIndex
      );
      nextTriggers[triggerIndex] = { ...trigger, actions: nextActions };
      return { ...prev, triggers: nextTriggers };
    });
    setDeletionRequest(null);
  };

  const promptActionRemoval = (triggerIndex, actionIndex) => {
    setDeletionRequest({
      type: "action",
      triggerIndex,
      actionIndex,
    });
  };

  const moveActionInTrigger = (triggerIndex, actionIndex, direction) => {
    setForm((prev) => {
      const nextTriggers = [...prev.triggers];
      const trigger = nextTriggers[triggerIndex];
      if (!trigger) return prev;
      const nextActions = [...(trigger.actions || [])];
      const targetIndex = actionIndex + direction;
      if (targetIndex < 0 || targetIndex >= nextActions.length) return prev;
      const temp = nextActions[targetIndex];
      nextActions[targetIndex] = nextActions[actionIndex];
      nextActions[actionIndex] = temp;
      nextTriggers[triggerIndex] = { ...trigger, actions: nextActions };
      return { ...prev, triggers: nextTriggers };
    });
  };

  const handleActionTypeChange = (triggerIndex, actionIndex, value) => {
    updateActionInTrigger(triggerIndex, actionIndex, { type: value });
  };

  const getStagesForAction = (action) => {
    const funnelId =
      action.payload?.targetFunnelId || form.targetFunnelId || selectedFunnelId;
    return funnels.find((item) => item.id === funnelId)?.stages ?? [];
  };

  const buildDefaultActionPayload = (type) => {
    switch (type) {
      case "assign_responsible":
        return {
          responsibleId: users[0]?.id || "",
        };
      case "create_task":
        return {
          taskType: "followup",
          note: defaultTaskNoteFor("followup"),
          dueInDays: "1",
          dueInHours: "",
          responsibleId: users[0]?.id || "",
          stageId: stageOptions[0]?.id || "",
        };
      case "set_card_fields":
        return {
          fields: {},
        };
      case "apply_tag":
        return {
          tagId:
            getTagsForFunnelId(
              showDestinationSection ? form.targetFunnelId : selectedFunnelId
            )[0]?.id || "",
        };
      case "stage_event":
        return {
          stageId: stageOptions[0]?.id || "",
          event: "stage_enter",
        };
      case "log_history":
        return {
          text: "Histórico automático",
        };
      default:
        return {};
    }
  };

  const getActionLabel = (type) =>
    ACTION_TYPES.find((item) => item.value === type)?.label || type;

  const renderActionExtraFields = (action, triggerIndex, actionIndex) => {
    const fields = action.payload?.fields || {};
    const actionTags = getActionTags(action);

    return (
      <>
        {action.type === "assign_responsible" && (
          <div className="mt-4 space-y-2">
            <Label>Responsável</Label>
            <Select
              value={action.payload?.responsibleId || ""}
              onChange={(event) =>
                updateActionPayload(triggerIndex, actionIndex, {
                  responsibleId: event.target.value,
                })
              }
            >
              <option value="">Selecione um responsável</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name || user.email || user.id}
                </option>
              ))}
            </Select>
          </div>
        )}
        {action.type === "create_task" && (
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label>Tipo de tarefa</Label>
              <Select
                value={action.payload?.taskType || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    taskType: event.target.value,
                    note: defaultTaskNoteFor(event.target.value),
                  })
                }
              >
                {TASK_ACTION_PRESETS.map((preset) => (
                  <option key={preset.key} value={preset.key}>
                    {preset.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nota</Label>
              <textarea
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                rows={2}
                value={action.payload?.note || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    note: event.target.value,
                  })
                }
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Prazo em dias</Label>
                <TextInput
                  type="number"
                  min={0}
                  value={action.payload?.dueInDays || ""}
                  onChange={(event) =>
                    updateActionPayload(triggerIndex, actionIndex, {
                      dueInDays: event.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Prazo em horas</Label>
                <TextInput
                  type="number"
                  min={0}
                  value={action.payload?.dueInHours || ""}
                  onChange={(event) =>
                    updateActionPayload(triggerIndex, actionIndex, {
                      dueInHours: event.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Responsável pela tarefa</Label>
              <Select
                value={action.payload?.responsibleId || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    responsibleId: event.target.value,
                  })
                }
              >
                <option value="">Selecione</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.email || user.id}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Etapa do funil</Label>
              <Select
                value={action.payload?.stageId || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    stageId: event.target.value,
                  })
                }
              >
                <option value="">Selecione a etapa</option>
                {getStagesForAction(action).map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-gray-400">
                Defina em qual etapa o card deve permanecer após criar a tarefa.
              </p>
            </div>
          </div>
        )}
        {action.type === "set_card_fields" && (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <TextInput
                type="number"
                value={fields.priority ?? ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    fields: {
                      ...fields,
                      priority: event.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <TextInput
                value={fields.status || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    fields: {
                      ...fields,
                      status: event.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Origem</Label>
              <TextInput
                value={fields.origin || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    fields: {
                      ...fields,
                      origin: event.target.value,
                    },
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Internal status</Label>
              <TextInput
                value={fields.internalStatus || ""}
                onChange={(event) =>
                  updateActionPayload(triggerIndex, actionIndex, {
                    fields: {
                      ...fields,
                      internalStatus: event.target.value,
                    },
                  })
                }
              />
            </div>
          </div>
        )}
        {action.type === "apply_tag" && (
          <div className="mt-4 space-y-2">
            <Label>Etiqueta</Label>
            <Select
              value={action.payload?.tagId || ""}
              onChange={(event) =>
                updateActionPayload(triggerIndex, actionIndex, {
                  tagId: event.target.value,
                })
              }
            >
              <option value="">Selecione uma etiqueta</option>
              {actionTags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </Select>
            {!actionTags.length && (
              <p className="text-xs text-gray-400">
                Nenhuma etiqueta foi carregada para este funil.
              </p>
            )}
          </div>
        )}
        {action.type === "log_history" && (
          <div className="mt-4 space-y-2">
            <Label>Texto da anotação</Label>
            <textarea
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
              rows={2}
              value={action.payload?.text || ""}
              onChange={(event) =>
                updateActionPayload(triggerIndex, actionIndex, {
                  text: event.target.value,
                })
              }
            />
          </div>
        )}
      </>
    );
  };

  const scrollToBuilder = () => {
    if (typeof document === "undefined") {
      return;
    }
    document
      .getElementById("automation-builder")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const normalizeActionPayload = (action) => {
    const payload = { ...action.payload };
    if (action.type === "create_task") {
      const dueISO = calculateDueDateFromOffset(
        payload.dueInDays,
        payload.dueInHours
      );
      if (dueISO) {
        payload.dueDate = dueISO;
      } else {
        delete payload.dueDate;
      }
      delete payload.dueInDays;
      delete payload.dueInHours;
    }
    return payload;
  };

  const buildTriggersPayload = () =>
    (form.triggers || [])
      .map((trigger) => {
        if (!trigger?.stageId) return null;
        const normalizedActions = (trigger.actions || []).map(
          (action, actionIndex) => ({
            type: action.type,
            payload: normalizeActionPayload(action),
            isActive: action.isActive !== false,
            order:
              Number.isFinite(Number(action.order)) && Number(action.order) >= 0
                ? Number(action.order)
                : actionIndex,
          })
        );
        if (!normalizedActions.length) return null;
        return {
          name: trigger.name || "",
          stageId: trigger.stageId,
          event: trigger.event || "stage_enter",
          actions: normalizedActions,
        };
      })
      .filter(Boolean);

  const updateSettings = (changes) => {
    setForm((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...changes },
    }));
  };

  const totalAutomations = automations.length;
  const activeAutomations = automations.filter(
    (automation) => automation.isActive
  ).length;
  const pausedAutomations = totalAutomations - activeAutomations;

  return (
    <>
      <div className="space-y-4 py-2">
        <CorporateBreadcrumb items={breadcrumbItems} />
        <div className="grid gap-4">
          <div className="space-y-6 lg:col-span-1">
            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-[0.3em]">
                    Funil monitorado
                  </p>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {
                      funnels?.find((funnel) => funnel.id == selectedFunnelId)
                        ?.name
                    }
                  </h2>
                </div>
              </div>

              <div className="mt-4 border-t border-gray-100/60 pt-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Histórico de execuções
                    </p>
                    <p className="text-xs text-gray-500">
                      Veja o registro das ações automatizadas sem sair desta
                      seção.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setIsHistoryModalOpen(true)}
                    disabled={!selectedAutomationId}
                  >
                    Ver histórico
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 lg:col-span-1">
            <div
              id="automation-builder"
              className="rounded-3xl border border-gray-100 bg-gradient-to-br from-white via-slate-50 to-white p-6 shadow-sm"
            >
              <div>
                <div className="grid gap-4 md:grid-cols-4">
                  {viewMode === "list" && (
                    <div className="md:col-span-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500">
                            Gatilhos
                          </p>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Lista de gatilhos
                          </h3>
                        </div>
                        <Button size="xs" onClick={addTrigger}>
                          <HiPlus className="mr-1 h-4 w-4" /> Novo gatilho
                        </Button>
                      </div>
                      {form.triggers.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          Crie um gatilho para iniciar a automação.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {form.triggers.map((trigger, index) => (
                            <div
                              key={`${trigger.stageId || "trigger"}-${index}`}
                              className={cx(
                                "rounded-2xl border p-3 shadow-sm transition",
                                "border-gray-200 hover:border-indigo-200  bg-white/70"
                              )}
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-orange-500">
                                  Gatilho {index + 1}
                                </p>
                                <div className="flex gap-1">
                                  <Button
                                    size="xs"
                                    onClick={() => openTriggerEditor(index)}
                                  >
                                    <div className="flex items-center gap-2">
                                      <FiEye />
                                      <span>Visualizar</span>
                                    </div>
                                  </Button>
                                  <Button
                                    size="xs"
                                    color="failure"
                                    onClick={() => promptTriggerRemoval(index)}
                                  >
                                    <div className="flex items-center">
                                      <FiTrash />
                                    </div>
                                  </Button>
                                </div>
                              </div>
                              <p className="text-sm font-semibold text-gray-900">
                                {trigger.name || `Gatilho ${index + 1}`}
                              </p>
                              <div className="flex justify-between mt-2">
                                <div className="flex gap-4">
                                  <p className="text-xs text-gray-500">
                                    <span className="font-semibold">
                                      Evento:
                                    </span>{" "}
                                    {getTriggerEventLabel(trigger.event)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    <span className="font-semibold">
                                      Etapa:
                                    </span>{" "}
                                    {getTriggerStageName(trigger)}
                                  </p>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {trigger.actions?.length || 0} ação(ões)
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {viewMode === "builder" && (
                    <div className="md:col-span-4">
                      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50 p-4 shadow-inner space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-indigo-500">
                              {`Gatilho ${activeTriggerIndex + 1}`}
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              Configure o gatilho selecionado
                            </p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label>Nome do gatilho</Label>
                            <TextInput
                              value={activeTrigger.name || ""}
                              onChange={(event) =>
                                updateTrigger(activeTriggerIndex, {
                                  name: event.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Evento</Label>
                            <Select
                              value={activeTrigger.event || "stage_enter"}
                              onChange={(event) =>
                                updateTrigger(activeTriggerIndex, {
                                  event: event.target.value,
                                })
                              }
                            >
                              {TRIGGER_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Etapa disparadora</Label>
                            <Select
                              value={activeTrigger.stageId || ""}
                              onChange={(event) =>
                                updateTrigger(activeTriggerIndex, {
                                  stageId: event.target.value,
                                })
                              }
                            >
                              <option value="">Selecione uma etapa</option>
                              {stageOptions.map((stage) => (
                                <option key={stage.id} value={stage.id}>
                                  {stage.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-white/60 p-3 space-y-3 shadow-inner">
                          <div className="flex items-center justify-between">
                            <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                              Ações deste gatilho
                            </p>
                            <Button
                              size="xs"
                              onClick={() =>
                                addActionToTrigger(activeTriggerIndex)
                              }
                            >
                              <HiPlus className="mr-1 h-4 w-4" />
                              Adicionar ação
                            </Button>
                          </div>
                          {activeTriggerActions.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              Nenhuma ação configurada. Clique para criar uma.
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {activeTriggerActions.map(
                                (action, actionIndex) => (
                                  <div
                                    key={`${activeTriggerIndex}-${action.id}-${actionIndex}`}
                                    className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50 p-3 shadow-sm"
                                  >
                                    {action.isEditing ? (
                                      <>
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                          <div>
                                            <p className="text-sm font-semibold text-gray-900">
                                              {ACTION_TYPES.find(
                                                (item) =>
                                                  item.value === action.type
                                              )?.label || action.type}
                                            </p>
                                          </div>
                                          <Badge
                                            color={
                                              action.isActive
                                                ? "success"
                                                : "gray"
                                            }
                                          >
                                            {action.isActive
                                              ? "Ativa"
                                              : "Pausada"}
                                          </Badge>
                                        </div>
                                        <div className="grid gap-3 md:grid-cols-2 mt-3">
                                          <div className="space-y-2">
                                            <Label>Tipo de ação</Label>
                                            <Select
                                              value={action.type}
                                              onChange={(event) =>
                                                handleActionTypeChange(
                                                  activeTriggerIndex,
                                                  actionIndex,
                                                  event.target.value
                                                )
                                              }
                                            >
                                              {ACTION_TYPES.map((type) => (
                                                <option
                                                  key={type.value}
                                                  value={type.value}
                                                >
                                                  {type.label}
                                                </option>
                                              ))}
                                            </Select>
                                          </div>
                                          {action.type === "move_card" && (
                                            <>
                                              <div className="space-y-2">
                                                <Label>Funil de destino</Label>
                                                <Select
                                                  value={
                                                    action.payload
                                                      ?.targetFunnelId ||
                                                    form.targetFunnelId ||
                                                    selectedFunnelId
                                                  }
                                                  onChange={(event) => {
                                                    const targetFunnelId =
                                                      event.target.value;
                                                    const fallbackStages =
                                                      funnels.find(
                                                        (item) =>
                                                          item.id ===
                                                          targetFunnelId
                                                      )?.stages ?? [];
                                                    updateActionPayload(
                                                      activeTriggerIndex,
                                                      actionIndex,
                                                      {
                                                        targetFunnelId,
                                                        targetStageId:
                                                          fallbackStages[0]
                                                            ?.id || "",
                                                      }
                                                    );
                                                    loadTagsForFunnel(
                                                      targetFunnelId
                                                    );
                                                  }}
                                                >
                                                  <option value="">
                                                    Selecione
                                                  </option>
                                                  {funnels.map((funnel) => (
                                                    <option
                                                      key={funnel.id}
                                                      value={funnel.id}
                                                    >
                                                      {funnel.name}
                                                    </option>
                                                  ))}
                                                </Select>
                                              </div>
                                              <div className="space-y-2">
                                                <Label>Etapa de destino</Label>
                                                <Select
                                                  value={
                                                    action.payload
                                                      ?.targetStageId || ""
                                                  }
                                                  onChange={(event) =>
                                                    updateActionPayload(
                                                      activeTriggerIndex,
                                                      actionIndex,
                                                      {
                                                        targetStageId:
                                                          event.target.value,
                                                      }
                                                    )
                                                  }
                                                >
                                                  <option value="">
                                                    Selecione
                                                  </option>
                                                  {getStagesForAction(
                                                    action
                                                  ).map((stage) => (
                                                    <option
                                                      key={stage.id}
                                                      value={stage.id}
                                                    >
                                                      {stage.name}
                                                    </option>
                                                  ))}
                                                </Select>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                        {action.type === "set_lead_status" && (
                                          <div className="space-y-2">
                                            <Label>Status alvo</Label>
                                            <Select
                                              value={
                                                action.payload?.status || ""
                                              }
                                              onChange={(event) =>
                                                updateActionPayload(
                                                  activeTriggerIndex,
                                                  actionIndex,
                                                  {
                                                    status: event.target.value,
                                                  }
                                                )
                                              }
                                            >
                                              <option value="">
                                                Selecione
                                              </option>
                                              {STATUS_OPTIONS.map((status) => (
                                                <option
                                                  key={status.value}
                                                  value={status.value}
                                                >
                                                  {status.label}
                                                </option>
                                              ))}
                                            </Select>
                                          </div>
                                        )}
                                        {action.type === "send_email" && (
                                          <>
                                            <div className="space-y-2">
                                              <Label>Destinatário</Label>
                                              <Select
                                                value={
                                                  action.payload?.recipient ||
                                                  "lead"
                                                }
                                                onChange={(event) =>
                                                  updateActionPayload(
                                                    activeTriggerIndex,
                                                    actionIndex,
                                                    {
                                                      recipient:
                                                        event.target.value,
                                                    }
                                                  )
                                                }
                                              >
                                                {EMAIL_RECIPIENTS.map(
                                                  (recipient) => (
                                                    <option
                                                      key={recipient.value}
                                                      value={recipient.value}
                                                    >
                                                      {recipient.label}
                                                    </option>
                                                  )
                                                )}
                                              </Select>
                                            </div>
                                            {action.payload?.recipient ===
                                              "custom" && (
                                              <div className="space-y-2">
                                                <Label>
                                                  E-mail personalizado
                                                </Label>
                                                <TextInput
                                                  type="email"
                                                  value={
                                                    action.payload
                                                      ?.recipientEmail || ""
                                                  }
                                                  onChange={(event) =>
                                                    updateActionPayload(
                                                      activeTriggerIndex,
                                                      actionIndex,
                                                      {
                                                        recipientEmail:
                                                          event.target.value,
                                                      }
                                                    )
                                                  }
                                                />
                                              </div>
                                            )}
                                            <div className="space-y-2">
                                              <Label>Assunto</Label>
                                              <TextInput
                                                value={
                                                  action.payload?.subject || ""
                                                }
                                                onChange={(event) =>
                                                  updateActionPayload(
                                                    activeTriggerIndex,
                                                    actionIndex,
                                                    {
                                                      subject:
                                                        event.target.value,
                                                    }
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                              <Label>Corpo do e-mail</Label>
                                              <textarea
                                                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                                                rows={3}
                                                value={
                                                  action.payload?.body || ""
                                                }
                                                onChange={(event) =>
                                                  updateActionPayload(
                                                    activeTriggerIndex,
                                                    actionIndex,
                                                    {
                                                      body: event.target.value,
                                                    }
                                                  )
                                                }
                                              />
                                            </div>
                                          </>
                                        )}
                                        {renderActionExtraFields(
                                          action,
                                          activeTriggerIndex,
                                          actionIndex
                                        )}
                                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                          <SimpleToggle
                                            checked={action.isActive}
                                            label={
                                              action.isActive
                                                ? "Ativa"
                                                : "Pausada"
                                            }
                                            onChange={(nextValue) =>
                                              updateActionInTrigger(
                                                activeTriggerIndex,
                                                actionIndex,
                                                { isActive: nextValue }
                                              )
                                            }
                                          />
                                          <div className="flex items-center gap-2">
                                            <Button
                                              size="xs"
                                              onClick={() =>
                                                setActionEditing(
                                                  activeTriggerIndex,
                                                  actionIndex,
                                                  false
                                                )
                                              }
                                            >
                                              Salvar ação
                                            </Button>
                                            <Button
                                              size="xs"
                                              color="failure"
                                              onClick={() =>
                                                promptActionRemoval(
                                                  activeTriggerIndex,
                                                  actionIndex
                                                )
                                              }
                                            >
                                              Cancelar
                                            </Button>
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <p className="text-sm font-semibold text-gray-900">
                                              {ACTION_TYPES.find(
                                                (item) =>
                                                  item.value === action.type
                                              )?.label || action.type}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {formatActionSummary(
                                                action,
                                                activeTrigger
                                              )}
                                            </p>
                                          </div>
                                          <Badge
                                            color={
                                              action.isActive
                                                ? "success"
                                                : "gray"
                                            }
                                          >
                                            {action.isActive
                                              ? "Ativa"
                                              : "Pausada"}
                                          </Badge>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                          <Button
                                            size="xs"
                                            onClick={() =>
                                              setActionEditing(
                                                activeTriggerIndex,
                                                actionIndex,
                                                true
                                              )
                                            }
                                          >
                                            Editar
                                          </Button>
                                          <Button
                                            color="light"
                                            size="xs"
                                            outline
                                            onClick={() =>
                                              moveActionInTrigger(
                                                activeTriggerIndex,
                                                actionIndex,
                                                -1
                                              )
                                            }
                                          >
                                            <HiChevronUp className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            color="light"
                                            size="xs"
                                            outline
                                            onClick={() =>
                                              moveActionInTrigger(
                                                activeTriggerIndex,
                                                actionIndex,
                                                1
                                              )
                                            }
                                          >
                                            <HiChevronDown className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            size="xs"
                                            color={"failure"}
                                            onClick={() =>
                                              promptActionRemoval(
                                                activeTriggerIndex,
                                                actionIndex
                                              )
                                            }
                                          >
                                            <HiOutlineTrash className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-3">
                        <Button
                          color="light"
                          onClick={() => setViewMode("list")}
                        >
                          Voltar à lista
                        </Button>
                        <Button
                          size="md"
                          onClick={handleSubmit}
                          disabled={saving}
                        >
                          {saving ? (
                            <span className="flex items-center gap-2">
                              <Spinner size="sm" /> Salvando...
                            </span>
                          ) : (
                            "Salvar automação"
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        size="lg"
        popup
      >
        <Modal.Header>
          <div className="px-2 text-sm">Histórico de execuções</div>
        </Modal.Header>
        <Modal.Body>
          {loadingLogs && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner size="sm" /> Buscando histórico...
            </div>
          )}
          {logError && <p className="text-sm text-red-600">{logError}</p>}
          {!selectedAutomationId ? (
            <p className="text-sm text-gray-500">
              Escolha uma automação na lista para ver o histórico.
            </p>
          ) : loadingLogs ? null : logs.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhuma execução registrada para essa automação ainda.
            </p>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="rounded-xl border border-gray-100 p-3 bg-gray-50"
                >
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span>
                      {new Date(log.createdAt).toLocaleString("pt-BR")}
                    </span>
                    <Badge
                      color={log.status === "success" ? "success" : "failure"}
                    >
                      {log.status === "success" ? "Sucesso" : "Falha"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700">
                    {log.actionType}
                    {log.message ? ` — ${log.message}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={Boolean(deletionRequest)}
        onClose={handleCancelDeletion}
        size="md"
        popup
      >
        <Modal.Header>
          <div className="px-2 text-base flex items-center mt-1">
            {deletionRequest?.type === "trigger"
              ? "Excluir gatilho"
              : "Excluir ação"}
          </div>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600 text-center">
            {deletionRequest?.type === "trigger"
              ? "Essa alteração apagará o gatilho e todas as ações relacionadas. Deseja continuar?"
              : "Essa ação será removida do gatilho. Deseja continuar?"}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button size="sm" color="failure" onClick={handleConfirmDeletion}>
              Confirmar
            </Button>
            <Button
              size="sm"
              className="bg-gray-500 text-white"
              onClick={handleCancelDeletion}
            >
              Cancelar
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AutomationPage;
